/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useState } from "react";
import { useStores } from "@/app/csr-src/models";
import { observer } from "mobx-react-lite";
import { Helmet } from "react-helmet";
import Styles from "./index.module.scss";
import OrderItemCard from "./components/orderItemCard";
import to from "await-to-js";
import { getCurrentOrder, saveAddress } from "@/app/csr-src/apis/checkout/checkout.api";
import PaymentListCard from "./components/paymentListCard";
import SafetyLoading from "./components/safetyLoading";
import { useLocation, useNavigate } from "react-router-dom";
import TransactionStep from "@/app/csr-src/components/transactionStep";
import ApplyCoupon from "./components/applyCoupon";
import AccountAddress from "@/common-components-src/js/address/accountAddress/index";
import UKMaskLoading from "@/common-components-src/components/UKMaskLoading/index";
import UKPageError from "@/common-components-src/components/UKPageError";
import UKToast from "@/common-components-src/components/UKToast/index";
import { ACTION_TYPE, ALL_ACTION_TYPE } from "@/app/csr-src/apis/checkout/constants/constants";
import { gotoDp } from "@/app/csr-src/utils/goto";
import PayButtons from "./components/payButtons";
import PaymentDetailCard from "./components/paymentDetailCard";
import BillingAddressCard from "./components/billingAddressCard";
import { useTranslation } from "react-i18next";
import { selectAll } from "@/app/csr-src/apis/cart/cart.api";
import { getUserUUIDFromCookie } from "@/utils/userUtils";
import { BodyParam, OperationProduct, OperationPromotion } from "@/app/csr-src/apis/cart/cart.interface";
import { uuidv4 } from "@/app/csr-src/utils/uuidUtils";
import { EXPOSURE_RECORD, IExpoTrackParamsRecord, sendClickTrack, sendPv } from "@/tracks/25281";

const Checkout = observer(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { checkoutStore } = useStores();

  const { creditCardStore, browserInfo } = checkoutStore;

  const [hasAddress, setHasAddress] = useState(false);

  const query = new URLSearchParams(useLocation().search);
  const queryParams = {
    immediatelyBuy: query.get("immediatelyBuy") === "true" ? true : false,
    id: query.get("ref") || "",
  };

  const [actionType, setActionType] = useState < string > ("");
  const [error, setError] = useState < string | null > (null);

  useEffect(() => {
    // 初始化信用卡表单
    creditCardStore.initCreditForm();
    checkoutStore.setSafetyLoading(false);
    checkoutStore.setIsModalAddNew(false);
    // 将路由参数保存到store
    checkoutStore.setQueryParams(queryParams);
    // 初始化结算页接口请求参数
    const params = getCurrentQueryParams({
      ...queryParams,
      paymentExtParam: JSON.stringify({
        disabledPayMethods: "",
        currentUserPayMethods: "",
      }),
      useBestCoupon: true,
    });
    // 结算页接口请求参数保存到store
    checkoutStore.setQueryCurrentOrder(params);
    // 浏览器信息
    initBrowserInfo();
    // 结算页PV
    sendPv.SettleAccounts_OrderNew({
      source: queryParams.immediatelyBuy ? "商详" : "购物车",
    });
  }, []);

  const initBrowserInfo = () => {
    const info: any = { ...browserInfo };
    info.screenWidth = screen.width;
    info.screenHeight = screen.height;
    info.userAgent = navigator.userAgent;
    info.timeZoneOffset = String(new Date().getTimezoneOffset());
    checkoutStore.setBrowserInfo({ ...info });
  };

  /**
   * type: ALL_ACTION_TYPE.COUPON | ALL_ACTION_TYPE.CODE | ALL_ACTION_TYPE.CART | ALL_ACTION_TYPE.INIT | ALL_ACTION_TYPE.CHANGE_ADDRESS
   * @param params Reload data
   */
  const onFresh = (params: any) => {

    switch (params.type) {
      // 优惠券仅接收通知
      case ALL_ACTION_TYPE.COUPON:
        loadCurrentOrder({
          useBestCoupon: false,
          actionType: ACTION_TYPE.COUPON,
          refreshOrderSource: params.refreshOrderSource,
        }); //
        break;
      case ALL_ACTION_TYPE.CODE:
        loadCurrentOrder({
          useBestCoupon: false,
          actionType: ACTION_TYPE.CODE,
          refreshOrderSource: params.refreshOrderSource,
        }); // 优惠码接收楼层数据
        break;
      case ALL_ACTION_TYPE.CART:
      case ALL_ACTION_TYPE.INIT:
        loadCurrentOrder({
          ...params,
        });
        break;

      case ALL_ACTION_TYPE.CHANGE_ADDRESS:
        loadSaveAddressOrder({
          ...params,
        });
        break;

      case ALL_ACTION_TYPE.CHANGE_PAYMENT:
        loadCurrentOrder({
          ...params,
        });
        break;
      default:
        // 加减购物车
        loadCurrentOrder();
        break;
    }
  };

  // 调用结算首页接口， 获取结算页信息
  const loadCurrentOrder = async (params?: any) => {
    UKMaskLoading.show();

    const requestData = getCurrentQueryParams(params);

    checkoutStore.setQueryCurrentOrder(requestData);

    const [err, res] = await to(getCurrentOrder(requestData));
    // Set currency && language
    checkoutStore.setLocalInfo(res?.localInfo || {});

    if (!err) {
      initData(res);
      const action = params?.actionType || ACTION_TYPE.ADDRESS + Math.random();
      setActionType(action);
    } else {
      console.log(err, "err-----");
      UKToast.show({
        content: t("common.message.networkError"),
        icon: "error",
        duration: 2000,
      });
      setError("Error");
    }
    UKMaskLoading.close();

    setHasAddress(true);
  };

  // 切换地址，保存地址，返回数据结构与currentOrder一致
  const loadSaveAddressOrder = async (params?: any) => {
    UKMaskLoading.show();

    const requestData = getCurrentQueryParams(params);

    checkoutStore.setQueryCurrentOrder(requestData);

    const [err, res] = await to(saveAddress(requestData));
    // Set currency && language
    checkoutStore.setLocalInfo(res?.localInfo || {});

    if (!err) {
      initData(res);
      const action = params?.actionType || ACTION_TYPE.ADDRESS + Math.random();
      setActionType(action);
    } else {
      console.log(err, "err-----");
      UKToast.show({
        content: t("common.message.networkError"),
        icon: "error",
        duration: 2000,
      });
      setError("Error");
    }
    UKMaskLoading.close();

    setHasAddress(true);
  };

  // add cart
  const addCart = async (skus: any[], userActionId: string) => {
    UKMaskLoading.show();

    const promotions: OperationPromotion[] = [];
    const products: OperationProduct[] = skus.map((sku) => {
      const randomId = uuidv4();
      const product: any = {
        skuId: sku.skuId,
        num: sku.num,
        itemType: 1,
        id: randomId,
      };
      const promotion: any = {
        id: randomId,
        num: sku.num,
        childList: [randomId],
      };
      if (sku.isHitSumPromo) {
        product.parentId = randomId;
        promotion.itemType = "3";
        promotion.promotionId = sku.sumPromoId;
      }

      promotions.push(promotion);

      return product;
    });

    const bodyParam: BodyParam = {
      verticalTag: "cn_ybxt_b2c",
      area: "",
      clientVersion: "1.0.0",
      latitude: "",
      longitude: "",
      client: "pc",
      cartuuid: getUserUUIDFromCookie(),
      operations: {
        products: products,
        promotions: promotions,
      },
      userActionId: userActionId,
      extendParamMap: {
        stock: true,
      },
    };
    // is buy now
    if (queryParams.immediatelyBuy) {
      bodyParam.refer = 7;
    }

    const [err, result] = await to(selectAll(bodyParam));
    if (!err) {
      onFresh({
        useBestCoupon: true,
        type: ALL_ACTION_TYPE.CART,
        actionType: ACTION_TYPE.CART + Math.random(),
      });
    }
  };

  const initData = (res: any) => {
    const { code } = res as any;

    const currentOrderFloorData = (res as any).data;

    console.log(currentOrderFloorData, "currentOrderFloorData");

    // CSBB-DM@1011-CD@2.200014 无地址用户

    switch (code) {
      // Cart is empty
      case "CSBB-DM@1012-CD@2.200001":
      case "CSBB-DM@1012-CD@2.600001":
        UKToast.show({
          content: t("common.message.noItems"),
          icon: "error",
          duration: 2000,
        });

        setError("Cart is empty");

        setTimeout(toBack, 2000);

        break;
      // Address error
      case "C-DM@60101-CD@3.RC0002":
        toBack();
        break;

      // 接口返回正常
      case "C-DM@60-CD@1.100000":
        // Image domain TODO delete
        checkoutStore.setImageDomain(currentOrderFloorData.imageDomain);
        // Order sku
        checkoutStore.setOrderList(currentOrderFloorData.venderList);
        if (currentOrderFloorData.venderList.length === 0) {
          toBack();
          return;
        }

        /** 是否全部为月黑风高品 */
        checkoutStore.setIsAllHappyHour(currentOrderFloorData.isAllHappyHour);
        // Payment
        checkoutStore.setPaymentList(currentOrderFloorData.payment);
        // zero info
        checkoutStore.setZeroInfo(currentOrderFloorData.zeroInfo);

        // Billing Address
        if (!checkoutStore.isBillingAddressEdit) {
          checkoutStore.setBillingAddress(currentOrderFloorData.userAddress);
        }

        // Coupon
        checkoutStore.setCouponInfo(currentOrderFloorData.couponInfo);

        /** ** ** ** ** Payment Detail start ** ** ** ** **/
        // RightBox Payment Details
        checkoutStore.setPaymentDetail(currentOrderFloorData.amountSummary);

        // setPaymentDetail(paymentDetail);
        /** ** ** ** ** **  Payment Detail end ** ** ** **  **/
        setError("");
        break;

      default:
        setError("Error");
        break;
    }
  };

  const getCurrentQueryParams = (params?: any) => {
    const requestData = JSON.parse(JSON.stringify(checkoutStore.queryCurrentOrder)) as any;

    if (!params) {
      return requestData;
    }
    if (Object.prototype.hasOwnProperty.call(params, "addressId")) {
      checkoutStore.setAddressId(Number(params.addressId));
      requestData.orderStr.id = params.addressId;
    }
    if (Object.prototype.hasOwnProperty.call(params, "useBestCoupon")) {
      requestData.orderStr.useBestCoupon = params.useBestCoupon;
    }
    if (Object.prototype.hasOwnProperty.call(params, "immediatelyBuy")) {
      requestData.immediatelyBuy = params.immediatelyBuy;
    }
    if (Object.prototype.hasOwnProperty.call(params, "paymentExtParam")) {
      requestData.orderStr.paymentExtParam = params.paymentExtParam;
    }
    if (params.refreshOrderSource) {
      requestData.refreshOrderSource = params.refreshOrderSource;
    } else {
      delete requestData.refreshOrderSource;
    }
    return requestData;
  };

  // 返回上一级
  const toBack = () => {
    if ((checkoutStore.queryParams as any).immediatelyBuy && checkoutStore.buySkuId) {
      gotoDp(checkoutStore.buySkuId);
    } else {
      navigate("/cart", { replace: true });
    }
  };
  // 支付方式切换点击埋点
  const paymentTrackAction = (params: { payWay: string,channelName: string }) => {
    sendClickTrack.SettleAccounts_OrderNew_PayWay(params);
  }


  return (
    <TransactionStep step={1} refresh={() => window.location.reload()}>
      <div className={Styles.checkoutPage}>
        <Helmet>
          <title>Complete Your Order | Secure Checkout | Joybuy</title>
          <meta
            name="description"
            content="Complete your purchase securely at Joybuy.com. Enjoy fast shipping and a delivery guarantee on all orders. Fast and reliable checkout for a seamless shopping experience."
          />
        </Helmet>
        {error ? (
          <div className={Styles.errorPage}>
            <UKPageError
              layout="row"
              clickTryAgain={() => window.location.reload()}
              text={t("common.message.refreshError")}
              textStyle={{
                width: "170px"
              }}
            />
          </div>
        ) : (
          <>
            <div className={Styles.leftBox}>
              <div
                exptag={"exp|" + EXPOSURE_RECORD.SettleAccounts_OrderNew_AddressExpo}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                data-exptag-json-param=""
              >
                <AccountAddress
                  loadCurrentOrder={(params?: any) => {
                    if (params?.isError) {
                      setError("Address error");
                      return;
                    }
                    onFresh({
                      ...params,
                      useBestCoupon: true,
                      actionType: ACTION_TYPE.ADDRESS + Math.random(),
                    });
                  }}
                />
              </div>
              {hasAddress ? (
                <>
                  {((checkoutStore.orderList as any) || []).map(
                    (
                      item: {
                        nodeList: any[];
                        shipingMethodList: any[];
                        shippingMethod: string;
                        promise: any;
                      },
                      index: number,
                    ) => {
                      return (
                        <div
                          key={index}
                          exptag={"exp|" + EXPOSURE_RECORD.SettleAccounts_OrderNew_ItemsExpo}
                          data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                          data-exptag-json-param={
                            JSON.stringify(checkoutStore.trackOrderList[index] as IExpoTrackParamsRecord["SettleAccounts_OrderNew_ItemsExpo"])
                          }
                        >
                          <OrderItemCard
                            skuList={item.nodeList}
                            promiseObj={item.promise}
                            addCart={addCart}
                            currentIndex={index}
                          />
                        </div>
                      );
                    },
                  )}
                  <div
                    exptag={"exp|" + EXPOSURE_RECORD.SettleAccounts_OrderNew_PayWayExpo}
                    data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                    data-exptag-json-param=""
                  >
                      <PaymentListCard onTrack={paymentTrackAction}
                        onFresh={(params: any) => {
                          onFresh({
                            paymentExtParam: JSON.stringify(params),
                            type: ALL_ACTION_TYPE.CHANGE_PAYMENT,
                            useBestCoupon: true,
                          })
                        }}
                      />
                  </div>
                  {!checkoutStore.isAllHappyHour && (
                    <div
                      exptag={"exp|" + EXPOSURE_RECORD.SettleAccounts_OrderNew_CouponExpo}
                      data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                        data-exptag-json-param={JSON.stringify({ ...checkoutStore.trackCouponInfo, tabName: "0"})}>
                      <ApplyCoupon
                        onFresh={onFresh}
                        actionType={actionType}
                        immediatelyBuy={queryParams.immediatelyBuy}
                      />
                    </div>
                  )}
                </>
              ) : null}
            </div>
            {hasAddress && (
              <PaymentDetailCard>
                <BillingAddressCard />
                <PayButtons
                  onFresh={onFresh}
                  toBack={toBack}
                  type="submitOrder"
                  addCart={addCart}
                  immediatelyBuy={queryParams.immediatelyBuy}
                />
              </PaymentDetailCard>
            )}
          </>
        )}
        {checkoutStore.safetyLoading && <SafetyLoading />}
      </div>
    </TransactionStep>
  );
});

export default Checkout;
